import path from 'node:path'
import * as p from '@clack/prompts'
import type { AddFieldCommandOptions } from '@core-engine/commands/runtime.js'
import {
  type AddFieldOption,
  type AddFieldPlacementTarget,
  applySlotToField,
  buildLeafField,
  buildPlacementTargets,
  deriveFieldNameFromLabel,
  ENTITY_SINGLE_ADD_FIELD_TYPES,
  FORM_ADD_FIELD_TYPES,
  type GeneratedSchemaField,
  type GeneratedSchemaKind,
  getSchemaDisplayName,
  insertFieldIntoTarget,
  isRequiredPersistedSchemaField,
  type LoadedAuthoredGeneratedSchema,
  loadAuthoredGeneratedSchema,
  NON_INTERACTIVE_CONTAINER_TYPES,
  parseAddFieldOptions,
  type RelationshipTargetKind,
  readRelationshipTargetKind,
  resolveDefaultPlacementTarget,
  validateAuthoredGeneratedSchema,
  validateGeneratedFieldName,
  writeAuthoredGeneratedSchema
} from '@core-engine/schema/add-field.js'
import { listSchemaNames } from '@core-engine/schema/schema-reader.js'
import { USER_RELATIONSHIP_TARGET } from '@core-engine/schema/system-relationships.js'
import type {
  FormColumn,
  FormField,
  FormSchema,
  Schema,
  SchemaColumn,
  SchemaField
} from '@core-engine/schema/types.js'
import { loadManifest, snapshotRootExists } from '@core-engine/snapshots/store.js'
import { stringifyProjectJson } from '@core-engine/utils/json-format.js'
import { resolveProjectPaths } from '@next-adapter/config/paths.js'
import { resolveConfig } from '@next-adapter/config.js'
import { getSchemaOwner } from '@plugin-engine/ownership.js'
import type { SchemaOwner } from '@plugin-engine/types.js'
import { runGenerateCommand } from './generate.js'

interface PreparedFieldInsertion {
  field: GeneratedSchemaField
  target: AddFieldPlacementTarget
  order: {
    before?: string
    after?: string
  }
  summary: string[]
}

function isInteractiveSession(): boolean {
  return Boolean(process.stdin.isTTY) && process.env.CI !== 'true'
}

function hasNonInteractiveFieldOptions(options: AddFieldCommandOptions): boolean {
  return Boolean(
    options.type ||
      options.field ||
      options.label ||
      options.slot ||
      options.step ||
      options.before ||
      options.after ||
      options.required ||
      options.multiple ||
      options.creatable ||
      options.relationship ||
      (options.option && options.option.length > 0)
  )
}

function fail(message: string): never {
  p.log.error(message)
  process.exit(1)
}

function cancel(message = 'Add field cancelled.'): never {
  p.cancel(message)
  process.exit(0)
}

function cancelIfNeeded<T>(value: T | symbol): T {
  if (p.isCancel(value)) {
    cancel()
  }
  return value
}

function schemaNameFromLoaded(loaded: LoadedAuthoredGeneratedSchema): string {
  const schemaName = loaded.schema.name
  return typeof schemaName === 'string' && schemaName.trim() ? schemaName : loaded.name
}

function resolveSchemaOwner(cwd: string, schemaName: string): SchemaOwner {
  return getSchemaOwner(cwd, schemaName) ?? (schemaName === 'settings' ? 'core' : 'user')
}

function pluginIdFromOwner(owner: SchemaOwner): string | null {
  return owner.startsWith('plugin:') ? owner.replace(/^plugin:/, '') : null
}

async function promptConfirm(message: string, initialValue = true): Promise<boolean> {
  const confirmed = await p.confirm({ message, initialValue })
  if (p.isCancel(confirmed)) {
    cancel()
  }
  return Boolean(confirmed)
}

async function promptText(message: string, defaultValue?: string): Promise<string> {
  const value = await p.text({
    message,
    defaultValue,
    validate(input) {
      if (!input || !String(input).trim()) {
        return 'Enter a value.'
      }
    }
  })

  return String(cancelIfNeeded(value)).trim()
}

async function promptOptionalText(message: string): Promise<string | undefined> {
  const value = await p.text({
    message,
    placeholder: 'Leave blank to skip'
  })

  const result = String(cancelIfNeeded(value)).trim()
  return result || undefined
}

async function promptSelectValue<T extends string>(
  message: string,
  options: Array<{ value: T; label: string }>,
  initialValue?: T
): Promise<T> {
  const value = await p.select<T>({
    message,
    options: options as never,
    initialValue
  })
  return cancelIfNeeded(value) as T
}

function formatKind(kind: RelationshipTargetKind): string {
  if (kind === 'user') return 'User'
  if (kind === 'form') return 'form'
  if (kind === 'single') return 'Single'
  return 'Entity'
}

function normalizeOptionList(options: AddFieldCommandOptions): AddFieldOption[] {
  try {
    return parseAddFieldOptions(options.option)
  } catch (error) {
    fail(error instanceof Error ? error.message : String(error))
  }
}

function validateRelationshipTarget(
  schemasDir: string,
  targetSchema: string | undefined,
  fieldType: string | undefined
): void {
  if (!targetSchema || fieldType !== 'relationship') {
    return
  }

  let targetKind: RelationshipTargetKind
  try {
    targetKind = readRelationshipTargetKind(schemasDir, targetSchema)
  } catch (error) {
    fail(`Relationship target error: ${error instanceof Error ? error.message : String(error)}`)
  }

  if (targetKind === 'form') {
    fail('Relationship fields can target Entity and Single schemas only, not form schemas.')
  }
}

function createNonInteractiveInsertion(
  loaded: LoadedAuthoredGeneratedSchema,
  schemasDir: string,
  options: AddFieldCommandOptions
): PreparedFieldInsertion {
  if (!options.type || !options.field || !options.label) {
    fail('Non-interactive add-field requires --type, --field, and --label.')
  }

  if (NON_INTERACTIVE_CONTAINER_TYPES.has(options.type)) {
    fail(`Non-interactive add-field does not support recursive "${options.type}" fields.`)
  }

  validateRelationshipTarget(schemasDir, options.relationship, options.type)

  const target = resolveDefaultPlacementTarget(loaded, {
    slot: options.slot,
    step: options.step
  })
  const field = applySlotToField(
    loaded,
    target,
    buildLeafField({
      kind: loaded.kind,
      type: options.type,
      name: options.field,
      label: options.label,
      required: options.required,
      multiple: options.multiple,
      creatable: options.creatable,
      options: normalizeOptionList(options),
      relationship: options.relationship
    }),
    options.slot
  )

  return {
    field,
    target,
    order: {
      before: options.before,
      after: options.after
    },
    summary: []
  }
}

async function createInteractiveInsertion(
  loaded: LoadedAuthoredGeneratedSchema,
  schemasDir: string
): Promise<PreparedFieldInsertion> {
  const targets = buildPlacementTargets(loaded)
  const target =
    targets.length === 1
      ? targets[0]!
      : await promptSelectValue(
          'Where should the new field be inserted?',
          targets.map((candidate) => ({
            value: candidate.id,
            label:
              candidate.scope === 'list-item'
                ? `${candidate.label} (list item scope)`
                : candidate.label
          }))
        ).then((targetId) => targets.find((candidate) => candidate.id === targetId) ?? targets[0]!)

  const order = await promptInsertionOrder(target)
  let field = await promptField(loaded.kind, schemasDir, target.scope)
  if (loaded.kind !== 'form' && target.id === 'root:fields') {
    const slot = await promptSelectValue(
      'Field slot',
      [
        { value: 'main', label: 'main' },
        { value: 'sidebar', label: 'sidebar' }
      ],
      'main'
    )
    field = applySlotToField(loaded, target, field, slot)
  }
  const summary = await promptSchemaIntegrationOptions(loaded, field, target)

  return {
    field,
    target,
    order,
    summary
  }
}

async function promptInsertionOrder(target: AddFieldPlacementTarget): Promise<{
  before?: string
  after?: string
}> {
  const fields = target.getFields()
  const namedFields = fields
    .map((field) => field.name)
    .filter((name): name is string => typeof name === 'string' && name.length > 0)

  if (namedFields.length === 0) {
    return {}
  }

  const order = await promptSelectValue('Where in that target?', [
    { value: 'end', label: 'End' },
    { value: 'before', label: 'Before field' },
    { value: 'after', label: 'After field' }
  ] as const)

  if (order === 'end') {
    return {}
  }

  const field = await promptSelectValue(
    order === 'before' ? 'Insert before which field?' : 'Insert after which field?',
    namedFields.map((name) => ({ value: name, label: name }))
  )

  return order === 'before' ? { before: field } : { after: field }
}

async function promptField(
  kind: GeneratedSchemaKind,
  schemasDir: string,
  scope: 'schema' | 'list-item',
  depth = 0
): Promise<GeneratedSchemaField> {
  const label = await promptText(depth === 0 ? 'Field label' : 'Child field label')
  const name = await promptFieldName(label)
  const type = await promptFieldType(kind)

  if (kind !== 'form' && ['group', 'section', 'list'].includes(type)) {
    return promptSchemaContainerField(kind, schemasDir, scope, name, label, type, depth)
  }

  if (kind !== 'form' && type === 'tabs') {
    return promptTabsField(kind, schemasDir, scope, name, label, depth)
  }

  if (kind === 'form' && ['group', 'list'].includes(type)) {
    return promptFormContainerField(kind, schemasDir, scope, name, label, type, depth)
  }

  const required = (await shouldPromptRequired(type))
    ? await promptConfirm('Required field?', false)
    : false
  const creatable =
    kind !== 'form' && type === 'select'
      ? await promptConfirm('Allow editors to create options?', false)
      : false
  const options = await promptFieldOptions(kind, type, creatable)
  const multiple = await promptMultiple(kind, type)
  const relationship = await promptRelationshipTarget(kind, type, schemasDir)

  const field = buildLeafField({
    kind,
    type,
    name,
    label,
    required,
    multiple,
    creatable,
    options,
    relationship
  })

  await applyAdvancedOptions(field, kind, type)
  return field
}

async function promptFieldName(label: string): Promise<string> {
  const defaultName = deriveFieldNameFromLabel(label)
  const name = await p.text({
    message: 'Field name',
    defaultValue: defaultName,
    validate(value) {
      const errors = validateGeneratedFieldName(String(value ?? ''))
      return errors[0]
    }
  })

  return String(cancelIfNeeded(name)).trim()
}

async function promptFieldType(kind: GeneratedSchemaKind): Promise<string> {
  const types = kind === 'form' ? FORM_ADD_FIELD_TYPES : ENTITY_SINGLE_ADD_FIELD_TYPES
  return promptSelectValue(
    'Field type',
    types.map((type) => ({
      value: type,
      label: type
    }))
  )
}

async function shouldPromptRequired(type: string): Promise<boolean> {
  return !['group', 'section', 'tabs', 'separator'].includes(type)
}

async function promptMultiple(kind: GeneratedSchemaKind, type: string): Promise<boolean> {
  if (kind === 'form') {
    return ['file', 'upload'].includes(type) ? promptConfirm('Allow multiple files?', false) : false
  }

  return ['select', 'relationship'].includes(type)
    ? promptConfirm('Allow multiple values?', false)
    : false
}

async function promptFieldOptions(
  kind: GeneratedSchemaKind,
  type: string,
  creatable: boolean
): Promise<AddFieldOption[]> {
  const requiresOptions =
    kind === 'form' ? ['select', 'radio', 'multiselect'].includes(type) : type === 'select'

  if (!requiresOptions) {
    return []
  }

  if (kind !== 'form' && type === 'select' && creatable) {
    const staticOptions = await promptConfirm('Add static default options?', false)
    if (!staticOptions) {
      return []
    }
  }

  const options: AddFieldOption[] = []
  while (true) {
    const addOption =
      options.length === 0 ? true : await promptConfirm('Add another option?', false)
    if (!addOption) {
      break
    }

    const option = await promptText('Option as value:Label')
    try {
      const parsed = parseInteractiveOption(option, options)
      options.push(parsed)
    } catch (error) {
      p.log.error(error instanceof Error ? error.message : String(error))
    }
  }

  return options
}

function parseInteractiveOption(value: string, existing: AddFieldOption[]): AddFieldOption {
  const [parsed] = parseAddFieldOptions([value])
  if (!parsed) {
    throw new Error(`Invalid option "${value}". Use value:Label.`)
  }
  if (existing.some((option) => option.value === parsed.value)) {
    throw new Error(`Duplicate option value "${parsed.value}".`)
  }
  return parsed
}

async function promptRelationshipTarget(
  kind: GeneratedSchemaKind,
  type: string,
  schemasDir: string
): Promise<string | undefined> {
  if (kind === 'form' || type !== 'relationship') {
    return undefined
  }

  const schemaNames = listSchemaNames(schemasDir).filter(
    (schemaName) => schemaName !== USER_RELATIONSHIP_TARGET
  )
  const targets = schemaNames
    .map((schemaName) => {
      try {
        const targetKind = readRelationshipTargetKind(schemasDir, schemaName)
        return targetKind === 'form'
          ? null
          : { schemaName, targetKind: targetKind as 'entity' | 'single' | 'user' }
      } catch {
        return null
      }
    })
    .filter((target): target is { schemaName: string; targetKind: 'entity' | 'single' | 'user' } =>
      Boolean(target)
    )

  targets.unshift({
    schemaName: USER_RELATIONSHIP_TARGET,
    targetKind: 'user'
  })

  if (targets.length === 0) {
    fail('No Entity or Single schemas are available as relationship targets.')
  }

  return promptSelectValue(
    'Relationship target',
    targets.map((target) => ({
      value: target.schemaName,
      label: `${target.schemaName} (${formatKind(target.targetKind)})`
    }))
  )
}

async function promptSchemaContainerField(
  kind: GeneratedSchemaKind,
  schemasDir: string,
  scope: 'schema' | 'list-item',
  name: string,
  label: string,
  type: string,
  depth: number
): Promise<SchemaField> {
  const field = {
    name,
    type,
    label,
    fields: []
  } as SchemaField

  await applyAdvancedOptions(field, kind, type)
  await promptChildFields(
    kind,
    schemasDir,
    type === 'list' ? 'list-item' : scope,
    field.fields!,
    depth
  )
  return field
}

async function promptFormContainerField(
  kind: GeneratedSchemaKind,
  schemasDir: string,
  scope: 'schema' | 'list-item',
  name: string,
  label: string,
  type: string,
  depth: number
): Promise<FormField> {
  const field = {
    name,
    type,
    label,
    fields: []
  } as FormField

  await applyAdvancedOptions(field, kind, type)
  await promptChildFields(
    kind,
    schemasDir,
    type === 'list' ? 'list-item' : scope,
    field.fields!,
    depth
  )
  return field
}

async function promptChildFields(
  kind: GeneratedSchemaKind,
  schemasDir: string,
  scope: 'schema' | 'list-item',
  fields: GeneratedSchemaField[],
  depth: number
): Promise<void> {
  while (true) {
    const addChild =
      fields.length === 0
        ? await promptConfirm('Add a child field?', true)
        : await promptConfirm('Add another child field?', false)

    if (!addChild) {
      if (fields.length === 0) {
        p.log.warn('Containers need at least one child field.')
        continue
      }
      return
    }

    fields.push(await promptField(kind, schemasDir, scope, depth + 1))
  }
}

async function promptTabsField(
  kind: GeneratedSchemaKind,
  schemasDir: string,
  scope: 'schema' | 'list-item',
  name: string,
  label: string,
  depth: number
): Promise<SchemaField> {
  const field = {
    name,
    type: 'tabs',
    label,
    tabs: []
  } as SchemaField

  await applyAdvancedOptions(field, kind, 'tabs')

  while (true) {
    const addTab =
      field.tabs!.length === 0
        ? await promptConfirm('Add a tab?', true)
        : await promptConfirm('Add another tab?', false)
    if (!addTab) {
      if (field.tabs!.length === 0) {
        p.log.warn('Tabs need at least one tab.')
        continue
      }
      return field
    }

    const tabLabel = await promptText('Tab label')
    const tabName = await promptFieldName(tabLabel)
    const layout = await promptSelectValue('Tab layout', [
      { value: 'fields', label: 'Single fields array' },
      { value: 'slot', label: 'Main/sidebar slots' }
    ] as const)

    if (layout === 'fields') {
      const tabFields: SchemaField[] = []
      await promptChildFields(kind, schemasDir, scope, tabFields, depth + 1)
      field.tabs!.push({ name: tabName, label: tabLabel, fields: tabFields })
    } else {
      const mainFields: SchemaField[] = []
      const sidebarFields: SchemaField[] = []
      while (true) {
        const addChild =
          mainFields.length + sidebarFields.length === 0
            ? await promptConfirm('Add a tab child field?', true)
            : await promptConfirm('Add another tab child field?', false)
        if (!addChild) {
          if (mainFields.length + sidebarFields.length === 0) {
            p.log.warn('Tabs need at least one child field.')
            continue
          }
          break
        }

        const slot = await promptSelectValue('Child field slot', [
          { value: 'main', label: 'main' },
          { value: 'sidebar', label: 'sidebar' }
        ] as const)
        const child = (await promptField(kind, schemasDir, scope, depth + 1)) as SchemaField
        if (slot === 'sidebar') {
          sidebarFields.push(child)
        } else {
          mainFields.push(child)
        }
      }
      field.tabs!.push({
        name: tabName,
        label: tabLabel,
        slot: {
          ...(mainFields.length > 0 ? { main: { fields: mainFields } } : {}),
          ...(sidebarFields.length > 0 ? { sidebar: { fields: sidebarFields } } : {})
        }
      })
    }
  }
}

async function applyAdvancedOptions(
  field: GeneratedSchemaField,
  kind: GeneratedSchemaKind,
  type: string
): Promise<void> {
  const configure = await promptConfirm('Configure advanced options?', false)
  if (!configure) {
    return
  }

  const record = field as unknown as Record<string, unknown>
  const omitLabel = await promptConfirm('Omit the label property?', false)
  if (omitLabel) {
    record.label = undefined
  }

  if (!['group', 'section', 'tabs', 'separator', 'list', 'dynamicFields'].includes(type)) {
    const placeholder = await promptOptionalText('Placeholder')
    if (placeholder) {
      record.placeholder = placeholder
    }
  }

  const hint = await promptOptionalText('Hint')
  if (hint) {
    record.hint = hint
  }

  if (['text', 'textarea', 'markdown', 'richtext'].includes(type)) {
    const height = await promptSelectValue('Height', [
      { value: 'auto', label: 'auto' },
      { value: 'fill', label: 'fill' }
    ] as const)
    if (height !== 'auto') {
      record.height = height
    }
  }

  if (kind !== 'form' && ['string', 'varchar', 'icon'].includes(type)) {
    const length = await promptOptionalText('Max length')
    if (length) {
      const parsed = Number(length)
      if (Number.isInteger(parsed) && parsed > 0) {
        record.length = parsed
      } else {
        p.log.warn('Skipped invalid length.')
      }
    }
  }

  if (kind !== 'form' && type === 'decimal') {
    const precision = await promptOptionalText('Precision')
    const scale = await promptOptionalText('Scale')
    const parsedPrecision = precision ? Number(precision) : undefined
    const parsedScale = scale ? Number(scale) : undefined
    if (
      parsedPrecision !== undefined &&
      parsedScale !== undefined &&
      Number.isInteger(parsedPrecision) &&
      Number.isInteger(parsedScale)
    ) {
      record.precision = parsedPrecision
      record.scale = parsedScale
    }
  }
}

async function promptSchemaIntegrationOptions(
  loaded: LoadedAuthoredGeneratedSchema,
  field: GeneratedSchemaField,
  target: AddFieldPlacementTarget
): Promise<string[]> {
  if (target.scope === 'list-item') {
    return []
  }

  if (loaded.kind === 'form') {
    return promptFormColumnOption(loaded.schema as FormSchema, field as FormField)
  }

  if (loaded.kind === 'single') {
    return []
  }

  return promptEntityIntegrationOptions(loaded.schema as Schema, field as SchemaField)
}

async function promptEntityIntegrationOptions(
  schema: Schema,
  field: SchemaField
): Promise<string[]> {
  const summary: string[] = []
  const columnType = schemaColumnTypeForField(field)
  if (columnType) {
    const addColumn = await promptConfirm('Add this field to the table columns?', false)
    if (addColumn) {
      schema.columns = schema.columns ?? []
      schema.columns.push({
        accessorKey: field.name,
        header: displayLabel(field),
        type: columnType
      })
      summary.push(`table column: ${field.name}`)
    }
  }

  if (['string', 'email', 'varchar', 'text', 'markdown', 'richtext'].includes(field.type)) {
    const addSearch = await promptConfirm('Add this field to search?', false)
    if (addSearch) {
      schema.search = schema.search ?? { fields: [] }
      schema.search.fields = Array.from(new Set([...schema.search.fields, field.name]))
      summary.push(`search field: ${field.name}`)
    }
  }

  if (field.type === 'select' || field.type === 'boolean') {
    const addFilter = await promptConfirm('Add this field as a list filter?', false)
    if (addFilter) {
      schema.filters = schema.filters ?? []
      schema.filters.push({
        field: field.name,
        label: displayLabel(field),
        type: field.type === 'boolean' ? 'boolean' : 'select',
        ...(field.type === 'select' && field.options?.length
          ? {
              source: 'static' as const,
              options: field.options
            }
          : field.type === 'select'
            ? { source: 'distinct' as const }
            : {})
      })
      summary.push(`filter: ${field.name}`)
    }
  }

  return summary
}

async function promptFormColumnOption(schema: FormSchema, field: FormField): Promise<string[]> {
  const columnType = formColumnTypeForField(field)
  if (!field.name || !columnType) {
    return []
  }

  const addColumn = await promptConfirm('Add this field to submission admin columns?', false)
  if (!addColumn) {
    return []
  }

  schema.columns = schema.columns ?? []
  const column: FormColumn = {
    accessorKey: field.name,
    header: displayLabel(field),
    type: columnType
  }
  schema.columns.push(column)
  return [`submission column: ${field.name}`]
}

function schemaColumnTypeForField(field: SchemaField): SchemaColumn['type'] | undefined {
  switch (field.type) {
    case 'image':
    case 'media':
    case 'video':
      return 'image'
    case 'date':
    case 'timestamp':
      return 'date'
    case 'number':
    case 'decimal':
      return 'number'
    case 'boolean':
      return 'boolean'
    case 'select':
      return 'badge'
    case 'email':
      return 'email'
    case 'string':
    case 'varchar':
    case 'text':
    case 'markdown':
    case 'richtext':
    case 'time':
    case 'icon':
    case 'relationship':
      return 'text'
    default:
      return undefined
  }
}

function formColumnTypeForField(field: FormField): FormColumn['type'] | undefined {
  switch (field.type) {
    case 'email':
      return 'email'
    case 'date':
      return 'date'
    case 'number':
      return 'number'
    case 'select':
    case 'radio':
    case 'checkbox':
    case 'multiselect':
      return 'badge'
    case 'text':
    case 'textarea':
    case 'phone':
    case 'url':
    case 'timezone':
      return 'text'
    default:
      return undefined
  }
}

function displayLabel(field: GeneratedSchemaField): string {
  return typeof field.label === 'string' && field.label.trim()
    ? field.label
    : typeof field.name === 'string'
      ? field.name
      : 'Field'
}

function printPreview(
  loaded: LoadedAuthoredGeneratedSchema,
  owner: SchemaOwner,
  firstTimeGeneration: boolean,
  insertion: PreparedFieldInsertion,
  options: AddFieldCommandOptions
): void {
  const pluginId = pluginIdFromOwner(owner)
  const lines = [
    `schema: ${loaded.name} (${formatKind(loaded.kind)})`,
    `display: ${getSchemaDisplayName(loaded)}`,
    `owner: ${owner}`,
    `target: ${insertion.target.label}`,
    `order: ${insertion.order.before ? `before ${insertion.order.before}` : insertion.order.after ? `after ${insertion.order.after}` : 'end'}`,
    `migration: ${options.skipMigration ? 'skip db:push' : 'run post-generate default'}`,
    `merge conflicts: ${options.acceptGenerated || options.yes ? 'accept generated output' : 'write conflict markers'}`
  ]

  if (pluginId) {
    lines.push(
      `warning: plugin-owned schema; uninstalling "${pluginId}" can remove this schema and generated output`
    )
  }
  if (firstTimeGeneration) {
    lines.push('warning: no per-schema snapshot manifest; generation will start from fresh output')
  }
  if (insertion.summary.length > 0) {
    lines.push(`schema integration: ${insertion.summary.join(', ')}`)
  } else {
    lines.push('schema integration: none')
  }

  p.note(
    `${lines.join('\n')}\n\n${stringifyProjectJson(insertion.field).trim()}`,
    'Add field preview'
  )
}

async function confirmWarningCases(
  owner: SchemaOwner,
  firstTimeGeneration: boolean,
  requiredPersistedField: boolean,
  nonInteractive: boolean,
  yes: boolean | undefined,
  extraWarnings: string[] = []
): Promise<void> {
  const warnings: string[] = [...extraWarnings]
  const pluginId = pluginIdFromOwner(owner)
  if (pluginId) {
    warnings.push(
      `Schema is plugin-owned by "${pluginId}". Removing that plugin can remove the customized schema JSON, generated files, snapshots, and ownership entry.`
    )
  }
  if (firstTimeGeneration) {
    warnings.push('Schema JSON exists but no per-schema snapshot manifest was found.')
  }
  if (requiredPersistedField) {
    warnings.push(
      'Required persisted Entity/Single fields may need manual handling for existing rows.'
    )
  }

  if (warnings.length === 0) {
    return
  }

  if (nonInteractive) {
    if (!yes) {
      fail(`${warnings.join('\n')}\nRe-run with --yes to confirm these warning cases.`)
    }
    return
  }

  p.note(warnings.join('\n'), 'Warnings')
  const confirmed = await promptConfirm('Continue with these warnings?', true)
  if (!confirmed) {
    cancel()
  }
}

function collectRelationshipTargetWarnings(
  cwd: string,
  owner: SchemaOwner,
  field: GeneratedSchemaField
): string[] {
  if (owner !== 'user') {
    return []
  }

  const targetNames = new Set<string>()
  collectRelationshipTargets(field, targetNames)

  return Array.from(targetNames)
    .map((targetName) => {
      const targetOwner = getSchemaOwner(cwd, targetName)
      const pluginId = targetOwner ? pluginIdFromOwner(targetOwner) : null
      return pluginId
        ? `This user-owned schema references plugin-owned schema "${targetName}". Removing plugin "${pluginId}" may affect that relationship.`
        : null
    })
    .filter((warning): warning is string => Boolean(warning))
}

function collectRelationshipTargets(field: GeneratedSchemaField, targets: Set<string>): void {
  if ('relationship' in field && typeof field.relationship === 'string') {
    targets.add(field.relationship)
  }

  if (field.fields) {
    for (const child of field.fields) {
      collectRelationshipTargets(child, targets)
    }
  }

  if ('tabs' in field && field.tabs) {
    for (const tab of field.tabs) {
      for (const child of tab.fields ?? []) {
        collectRelationshipTargets(child, targets)
      }
      for (const child of tab.slot?.main?.fields ?? []) {
        collectRelationshipTargets(child, targets)
      }
      for (const child of tab.slot?.sidebar?.fields ?? []) {
        collectRelationshipTargets(child, targets)
      }
    }
  }
}

async function resolveSchemaNameInteractively(
  schemasDir: string,
  schemaName: string | undefined
): Promise<string> {
  if (schemaName) {
    return schemaName
  }

  if (!isInteractiveSession()) {
    fail('Schema name is required in non-interactive sessions.')
  }

  const schemaNames = listSchemaNames(schemasDir)
  if (schemaNames.length === 0) {
    fail(`No schemas found in ${schemasDir}.`)
  }

  return promptSelectValue(
    'Generated schema',
    schemaNames.map((name) => ({ value: name, label: name }))
  )
}

export async function runAddFieldCommand(
  schemaName: string | undefined,
  options: AddFieldCommandOptions
): Promise<void> {
  const cwd = options.cwd ? path.resolve(options.cwd) : process.cwd()
  const nonInteractive = hasNonInteractiveFieldOptions(options)

  if (!snapshotRootExists(cwd)) {
    fail(
      'Project state is unsupported or incomplete. Re-run `betterstart init --force` to create the current snapshot-backed layout.'
    )
  }

  const config = await resolveConfig(cwd)
  const paths = resolveProjectPaths(config)
  const schemasDir = path.join(cwd, ...paths.schemasDir.split('/'))
  const selectedSchemaName = await resolveSchemaNameInteractively(schemasDir, schemaName)

  let loaded: LoadedAuthoredGeneratedSchema
  try {
    loaded = loadAuthoredGeneratedSchema(schemasDir, selectedSchemaName)
  } catch (error) {
    fail(error instanceof Error ? error.message : String(error))
  }

  if (!nonInteractive && !isInteractiveSession()) {
    fail('Interactive add-field requires a TTY. Provide --type, --field, and --label instead.')
  }

  const owner = resolveSchemaOwner(cwd, schemaNameFromLoaded(loaded))
  const firstTimeGeneration = !loadManifest(cwd, schemaNameFromLoaded(loaded))
  let insertion: PreparedFieldInsertion
  try {
    insertion = nonInteractive
      ? createNonInteractiveInsertion(loaded, schemasDir, options)
      : await createInteractiveInsertion(loaded, schemasDir)
  } catch (error) {
    fail(error instanceof Error ? error.message : String(error))
  }

  const requiredPersistedField = isRequiredPersistedSchemaField(
    loaded,
    insertion.field,
    insertion.target
  )

  await confirmWarningCases(
    owner,
    firstTimeGeneration,
    requiredPersistedField,
    nonInteractive,
    options.yes,
    collectRelationshipTargetWarnings(cwd, owner, insertion.field)
  )

  try {
    insertFieldIntoTarget(insertion.target, insertion.field, insertion.order)
  } catch (error) {
    fail(error instanceof Error ? error.message : String(error))
  }

  const validationErrors = validateAuthoredGeneratedSchema(loaded)
  if (validationErrors.length > 0) {
    fail(
      `Schema validation failed after insertion:\n${validationErrors.map((error) => `  - ${error}`).join('\n')}`
    )
  }

  printPreview(loaded, owner, firstTimeGeneration, insertion, options)

  if (!nonInteractive && !options.yes) {
    const confirmed = await promptConfirm('Write schema JSON and regenerate this schema?', true)
    if (!confirmed) {
      cancel()
    }
  }

  writeAuthoredGeneratedSchema(loaded)
  p.log.success(`Updated ${path.relative(cwd, loaded.filePath)}`)

  try {
    await runGenerateCommand(selectedSchemaName, {
      force: false,
      acceptGenerated: Boolean(options.acceptGenerated || options.yes),
      all: false,
      interactive: true,
      skipMigration: Boolean(options.skipMigration),
      cwd
    })
  } catch (error) {
    p.log.error(error instanceof Error ? error.message : String(error))
    p.log.info(
      `Schema JSON was kept. Re-run \`betterstart generate ${selectedSchemaName}${options.skipMigration ? ' --skip-migration' : ''}\` after resolving the issue.`
    )
    process.exit(1)
  }
}
